import { SolIcon } from '@sol/icons';
import { ObjectTools } from '../../tools/object-tools';

export type SolIconSize = '12' | '13' | '14' | '16' | '20' | '24' | '32' | '64' | 'auto';

/** The container drawn behind the glyph; it takes the icon's own colour rather than naming one. */
export type SolIconShape =
  'none' | 'square-subtle' | 'square-solid' | 'square-outline' | 'circle-subtle' | 'circle-solid' | 'circle-outline';

export class SolIconStyle {
  private constructor(
    public readonly name: SolIcon | undefined,
    public readonly size: SolIconSize,
    public readonly shape: SolIconShape,
    public readonly href: string | undefined,
    public readonly isSpinner: boolean,
    public readonly hostClass: string,
  ) {}

  private static readonly shapeClasses: Record<SolIconShape, string[]> = {
    none: [],
    'square-subtle': ['sol-icon-shaped', 'sol-icon-shape-square'],
    'square-solid': ['sol-icon-shaped', 'sol-icon-shape-square', 'sol-icon-fill-solid'],
    'square-outline': ['sol-icon-shaped', 'sol-icon-shape-square', 'sol-icon-fill-solid', 'sol-icon-outline'],
    'circle-subtle': ['sol-icon-shaped', 'sol-icon-shape-circle'],
    'circle-solid': ['sol-icon-shaped', 'sol-icon-shape-circle', 'sol-icon-fill-solid'],
    'circle-outline': ['sol-icon-shaped', 'sol-icon-shape-circle', 'sol-icon-fill-solid', 'sol-icon-outline'],
  };

  public static of(): SolIconStyle {
    return new SolIconStyle(undefined, '16', 'none', undefined, false, '').recalculateHref().recalculateHostClass();
  }

  public withName(name: SolIcon | undefined): SolIconStyle {
    return this.copy({ name }).recalculateHref().recalculateIsSpinner();
  }

  public withSize(size: SolIconSize | undefined): SolIconStyle {
    return this.copy({ size: size ?? '16' }).recalculateHostClass();
  }

  public withShape(shape: SolIconShape | undefined): SolIconStyle {
    return this.copy({ shape: shape ?? 'none' }).recalculateHostClass();
  }

  private recalculateHref(): SolIconStyle {
    return this.copy({
      href: this.name === undefined ? undefined : `./public/icons/${this.name.replaceAll('_', '-')}.svg#icon`,
    });
  }

  private recalculateIsSpinner(): SolIconStyle {
    return this.copy({ isSpinner: this.name === SolIcon.spinner });
  }

  private recalculateHostClass(): SolIconStyle {
    const classes = ['sol-icon', `sol-icon-${this.size}`, ...SolIconStyle.shapeClasses[this.shape]];
    return this.copy({ hostClass: classes.join(' ') });
  }

  private copy(props: Partial<SolIconStyle>): SolIconStyle {
    return ObjectTools.copy<SolIconStyle>(this, props);
  }
}
